import { useId } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import type { VersionLabel } from "../../../api/content/versionDecoders";
import type { ContentVersion } from "../../../api/content/workflowsApi";
import type { LoadState } from "../../../shared/loading/useLoad";
import { useProjects } from "../../../projects/projectContext";
import { useContentVersionName } from "../../versions/labels/useContentVersionName";
import Chip from "../../../ui/feedback/Chip";
import VersionSelector from "../../../ui/controls/VersionSelector";
import { labelOf } from "../../versions/labels/useVersionLabels";
import { modelVersionText } from "../../versions/labels/versionChoiceText";
import { resolveModelVersion } from "../modelVersions";
import { versionedPagePath, type VersionedPage } from "./dataModelPages";

interface ModelVersionPickerProps {
  page: VersionedPage;
  /** Newest first. */
  versions: readonly ContentVersion[];
  labels: LoadState<ReadonlyMap<string, VersionLabel | null>>;
  chosenVersion: string | null;
}

/** The header's model version picker, as GitHub's branch picker above the content and v1's
 * version selector: the one place the version shows, by release number (F77). Every choice
 * pins an exact version in the address (NFR-16). */
export default function ModelVersionPicker({ page, versions, labels, chosenVersion }: ModelVersionPickerProps) {
  const { t, i18n } = useTranslation();
  const versionId = useId();
  const navigate = useNavigate();
  const { search } = useLocation();
  const resolved = resolveModelVersion(versions, chosenVersion);
  const projectId = useProjects().selectedProjectId;
  const pinned = useContentVersionName(projectId, resolved?.listed === undefined ? resolved?.commit ?? null : null);
  if (resolved === null) return null;
  return (
    <div className="flex w-full max-w-sm items-end gap-2">
      <VersionSelector
        id={versionId}
        label={t("ontologyViewer.version")}
        value={resolved.commit}
        options={[
          ...versions.map((version, index) => ({
            value: version.commit,
            label: modelVersionText(labelOf(labels, version.reference), version, index === 0 ? [t("ontologyViewer.latestVersion")] : [], i18n.resolvedLanguage),
          })),
          // A pinned version older than the listed page stays choosable.
          ...(resolved.listed === undefined ? [{ value: resolved.commit, label: pinned.name }] : []),
        ]}
        onChange={(version) => {
          // The terms keep their view (display, kind, module); a term of another version may not exist.
          if (version !== "") void navigate(`${versionedPagePath(page, version)}${search}`);
        }}
        placeholder={t("ontologyViewer.chooseVersion")}
      />
      {resolved.isLatest ? null : <Chip tone="warning" className="shrink-0">{t("ontologyViewer.olderVersion")}</Chip>}
    </div>
  );
}
